// src/components/DdosTesting/TestResults.jsx
import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
    if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K';
    return num.toString();
};

const TestResults = ({ results }) => {
    const { t } = useLanguage();
    const [isExpanded, setIsExpanded] = useState(false);

    if (!results) {
        return (
            <div className="bg-[#0e0e0e] border border-white/[0.06] rounded-3xl p-8 text-center">
                <div className="text-4xl mb-3 opacity-20">📊</div>
                <p className="text-gray-600 text-sm">{t('ddosNoTestResults') || 'No test results yet'}</p>
            </div>
        );
    }

    const isSuccess = results.success;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
        >
            <div className={`relative overflow-hidden bg-[#0e0e0e] border rounded-2xl sm:rounded-3xl ${isSuccess ? 'border-emerald-500/15' : 'border-red-500/15'
                }`}>
                {/* Top accent */}
                <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent ${isSuccess ? 'via-emerald-400/40' : 'via-red-400/40'
                    } to-transparent`} />

                <div className="p-4 sm:p-8">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                        <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isSuccess ? 'bg-emerald-500/10 border border-emerald-500/15' : 'bg-red-500/10 border border-red-500/15'
                                }`}>
                                <i className={`fas ${isSuccess ? 'fa-check-circle text-emerald-400' : 'fa-times-circle text-red-400'} text-xl`}></i>
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white">{t('ddosLastTestResult') || 'Test Results'}</h3>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className={`text-xs font-bold ${isSuccess ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {isSuccess ? (t('ddosSuccessful') || 'SUCCESS') : (t('ddosFailed') || 'FAILED')}
                                    </span>
                                    <span className="text-[10px] text-gray-600">
                                        {results.timestamp || new Date().toLocaleTimeString()}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="text-sm text-gray-500 hover:text-gray-300 transition-colors duration-150 flex items-center gap-2"
                        >
                            <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-xs`}></i>
                            {isExpanded ? (t('ddosShowLess') || 'Less') : (t('ddosShowDetails') || 'Details')}
                        </button>
                    </div>

                    {/* Summary Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                        <div className="bg-[#0a0a0a] border border-white/[0.04] rounded-xl p-4">
                            <span className="text-[10px] text-gray-600 uppercase tracking-wider">{t('ddosTargetStatusAfter') || 'Final Status'}</span>
                            <div className="text-lg font-bold text-white mt-1">{results.finalTargetStatus || t('ddosUnknown') || 'Unknown'}</div>
                        </div>
                        <div className="bg-[#0a0a0a] border border-white/[0.04] rounded-xl p-4">
                            <span className="text-[10px] text-gray-600 uppercase tracking-wider">{t('ddosTestType') || 'Attack Type'}</span>
                            <div className="text-lg font-bold text-white mt-1">{results.attackType || 'N/A'}</div>
                        </div>
                        <div className="bg-[#0a0a0a] border border-white/[0.04] rounded-xl p-4">
                            <span className="text-[10px] text-gray-600 uppercase tracking-wider">{t('ddosDuration') || 'Duration'}</span>
                            <div className="text-lg font-bold text-white mt-1">{results.duration || 'N/A'}</div>
                        </div>
                    </div>

                    {/* Summary Message */}
                    {results.summaryMessage && (
                        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.04] mb-4">
                            <p className="text-sm text-gray-400">{results.summaryMessage}</p>
                        </div>
                    )}

                    {/* Expanded Details */}
                    <AnimatePresence>
                        {isExpanded && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.25 }}
                                className="overflow-hidden"
                            >
                                <div className="pt-4 border-t border-white/[0.04]">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Attack Metrics */}
                                        <div className="space-y-3">
                                            <h4 className="text-xs font-bold text-red-400 uppercase tracking-wider">{t('ddosAttackMetrics') || 'Attack Metrics'}</h4>
                                            {[
                                                [t('ddosMaxPackets') || 'Max Packets/s', formatNumber(results.maxPacketsPerSec)],
                                                [t('ddosAvgBandwidth') || 'Avg Bandwidth', results.avgBandwidth || 'N/A'],
                                                [t('ddosTotalDataSent') || 'Total Data', results.totalDataSent || 'N/A'],
                                            ].map(([label, val], i) => (
                                                <div key={i} className="flex justify-between text-sm">
                                                    <span className="text-gray-500">{label}</span>
                                                    <span className="text-white font-medium">{val}</span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Server Response */}
                                        <div className="space-y-3">
                                            <h4 className="text-xs font-bold text-orange-400 uppercase tracking-wider">{t('ddosServerResponse') || 'Server Response'}</h4>
                                            {[
                                                [t('ddosHighestPing') || 'Max Ping', results.maxPing ? `${results.maxPing}ms` : 'N/A'],
                                                [t('ddosTimeouts') || 'Timeouts', results.timeoutCount ?? 'N/A'],
                                                [t('ddosLowestPlayers') || 'Min Players', formatNumber(results.minPlayersOnline)],
                                            ].map(([label, val], i) => (
                                                <div key={i} className="flex justify-between text-sm">
                                                    <span className="text-gray-500">{label}</span>
                                                    <span className="text-white font-medium">{val}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Vulnerabilities */}
                                    {results.vulnerabilitiesFound?.length > 0 && (
                                        <div className="mt-5 p-4 rounded-xl bg-orange-500/[0.04] border border-orange-500/10">
                                            <h4 className="text-xs font-bold text-orange-400 uppercase tracking-wider mb-3">
                                                {t('ddosVulnerabilitiesFound') || 'Vulnerabilities Found'}
                                            </h4>
                                            <ul className="space-y-1.5">
                                                {results.vulnerabilitiesFound.map((vuln, i) => (
                                                    <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
                                                        {vuln}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    <div className="mt-5 flex justify-end">
                                        <button className="px-4 py-2 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] rounded-xl text-xs text-gray-400 transition-colors duration-150 active:scale-95">
                                            <i className="fas fa-file-export mr-2"></i>
                                            {t('ddosExportDetails') || 'Export'}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </motion.div>
    );
};

export default TestResults;